import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import type { CarePlanScenario } from '@/hooks/useCarePlan';

interface CostComparisonChartProps {
  scenarios: CarePlanScenario[];
}

function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

export function CostComparisonChart({ scenarios }: CostComparisonChartProps) {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const data = useMemo(() =>
    scenarios
      .filter(s => s.optimized_scenario_cost || s.national_avg_scenario_cost)
      .map(s => ({
        name: (s.scenario_name || 'Scenario').length > 25
          ? (s.scenario_name || 'Scenario').slice(0, 25) + '...'
          : (s.scenario_name || 'Scenario'),
        optimized: s.optimized_scenario_cost || 0,
        national: s.national_avg_scenario_cost || 0,
      })),
    [scenarios]
  );

  if (data.length === 0) return null;

  const optimizedColor = isAhp ? (ahpDark ? '#7da6d4' : '#002868') : '#22c55e';
  const nationalColor = isAhp ? (ahpDark ? '#e85d6c' : '#B22234') : '#ef4444';

  return (
    <div className="space-y-3">
      <h2 className={`text-lg font-semibold ${isAhp ? (ahpDark ? 'text-[#f0ebe3]' : 'text-gray-900') : 'text-foreground'}`}>
        Cost Comparison
      </h2>

      <div className={`rounded-xl border p-4 ${
        isAhp ? (ahpDark ? 'border-[rgba(197,165,90,0.2)] bg-[#0f1d2f]' : 'border-gray-200 bg-white') : 'border-foreground/10 bg-card'
      }`}>
        {/* Legend */}
        <div className="flex items-center gap-4 mb-4 text-xs">
          <span className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: optimizedColor }} />
            <span className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>Optimized</span>
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: nationalColor }} />
            <span className={isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}>National Average</span>
          </span>
        </div>

        <ResponsiveContainer width="100%" height={Math.max(200, data.length * 80)}>
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 10, top: 5, bottom: 5 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={isAhp ? (ahpDark ? 'rgba(197,165,90,0.1)' : '#e5e7eb') : 'rgba(255,255,255,0.1)'}
              horizontal={false}
            />
            <XAxis
              type="number"
              tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              stroke={isAhp ? (ahpDark ? 'rgba(255,255,255,0.3)' : '#9ca3af') : 'rgba(255,255,255,0.4)'}
              fontSize={11}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={150}
              stroke={isAhp ? (ahpDark ? 'rgba(255,255,255,0.3)' : '#9ca3af') : 'rgba(255,255,255,0.4)'}
              fontSize={11}
              tick={{ fill: isAhp ? (ahpDark ? 'rgba(255,255,255,0.7)' : '#374151') : 'rgba(255,255,255,0.7)' }}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatUsd(value),
                name === 'optimized' ? 'Optimized' : 'National Average'
              ]}
              contentStyle={{
                backgroundColor: isAhp ? (ahpDark ? '#0f1d2f' : '#fff') : '#1a1a2e',
                border: isAhp ? (ahpDark ? '1px solid rgba(197,165,90,0.2)' : '1px solid #e5e7eb') : '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8,
                fontSize: 12,
                color: isAhp ? (ahpDark ? '#f0ebe3' : '#111') : '#fff',
              }}
            />
            <Bar dataKey="optimized" fill={optimizedColor} radius={[0, 4, 4, 0]} barSize={20} />
            <Bar dataKey="national" fill={nationalColor} radius={[0, 4, 4, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
